import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Phone, Mail } from 'lucide-react';
import { orderRide } from '../assets/images';

const FinancementCommercialPage: React.FC = () => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    company: '',
    message: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log(formData);
    // Handle form submission
  };

  return (
    <div className="pt-24">
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <h1 className="text-4xl font-bold text-primary-950 mb-6">{t('financing.commercial.title')}</h1>
              <h2 className="text-2xl font-semibold mb-6">{t('financing.commercial.forWho')}</h2>
              <ul className="space-y-4 mb-8">
                <li className="flex items-center space-x-3">
                  <span className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center text-teal-600">✓</span>
                  <span>{t('financing.commercial.clients.selfEmployed')}</span>
                </li>
                <li className="flex items-center space-x-3">
                  <span className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center text-teal-600">✓</span>
                  <span>{t('financing.commercial.clients.smeOwner')}</span>
                </li>
                <li className="flex items-center space-x-3">
                  <span className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center text-teal-600">✓</span>
                  <span>{t('financing.commercial.clients.commercialFleet')}</span>
                </li>
                <li className="flex items-center space-x-3">
                  <span className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center text-teal-600">✓</span>
                  <span>{t('financing.commercial.clients.transportVehicle')}</span>
                </li>
              </ul>
              <p className="text-lg text-gray-600 mb-8">
                {t('financing.commercial.flexibleSolutions')}
              </p>
            </div>
            <div className="relative">
              <img 
                src={orderRide}
                alt="Business team"
                className="w-3/4 h-auto mx-auto"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-black rounded-full flex items-center justify-center">
                <Phone className="text-white" size={24} />
              </div>
              <h3 className="text-lg font-semibold mb-2">{t('financing.commercial.steps.call.title')}</h3>
              <p className="text-gray-600">{t('financing.commercial.steps.call.description')}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-black rounded-full flex items-center justify-center">
                <span className="text-white text-2xl">✓</span>
              </div>
              <h3 className="text-lg font-semibold mb-2">{t('financing.commercial.steps.approved.title')}</h3>
              <p className="text-gray-600">{t('financing.commercial.steps.approved.description')}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-black rounded-full flex items-center justify-center">
                <span className="text-white text-2xl">🚗</span>
              </div>
              <h3 className="text-lg font-semibold mb-2">{t('financing.commercial.steps.findVehicle.title')}</h3>
              <p className="text-gray-600">{t('financing.commercial.steps.findVehicle.description')}</p>
            </div>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-bold text-primary-950 mb-6">{t('contact.getInTouch')}</h2>
              <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <input
                  type="text"
                  name="name"
                  placeholder={t('contact.name')}
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                />
                <input
                  type="tel"
                  name="phone"
                  placeholder={t('contact.phone')}
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                />
                <input
                  type="email"
                  name="email"
                  placeholder={t('contact.email')}
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                />
                <input
                  type="text"
                  name="company"
                  placeholder={t('financing.commercial.form.company')}
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                />
                <textarea
                  name="message"
                  placeholder={t('contact.message')}
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black md:col-span-2"
                  rows={4}
                ></textarea>
                <button
                  type="submit"
                  className="w-full md:col-span-2 px-8 py-3 bg-black hover:bg-gray-900 text-white font-medium rounded-md transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
                >
                  {t('contact.send')}
                </button>
              </form>

              <div className="mt-8 pt-8 border-t border-gray-200">
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Phone className="text-black" size={20} />
                    <span>450 805-3277</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Mail className="text-black" size={20} />
                    <span>info@financesunshine.com</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FinancementCommercialPage;